import { useEffect, useState } from "react"
import { collection, query, where, onSnapshot, Timestamp } from "firebase/firestore"
import { db } from "@/lib/firebase"

export interface Deal {
  id: string
  dealTitle: string
  dealDescription: string
  dealBannerUrl: string
  identifier: string
  discountPercentage: number
  validFrom: Date | null
  validTo: Date | null
  validDates: Date[]
  selectedFlashIds?: string[]
  isAvailable?: boolean
  createdAt: Date
  artistId?: string
  address: string
  city: string
  country: string
}

export function useDealByIdentifier(identifier: string | undefined) {
  const [deal, setDeal] = useState<Deal | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    if (!identifier) {
      setDeal(null)
      setLoading(false)
      return
    }

    setLoading(true)
    
    const q = query(
      collection(db, "deals"),
      where("identifier", "==", identifier)
    )
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const dealDoc = snapshot.docs[0] // Should only be one deal with this identifier
        const data = dealDoc.data()
        const dealData: Deal = {
          id: dealDoc.id,
          dealTitle: data.dealTitle as string,
          dealDescription: data.dealDescription as string,
          dealBannerUrl: data.dealBannerUrl as string,
          identifier: data.identifier as string,
          discountPercentage: data.discountPercentage as number,
          validFrom: data.validFrom instanceof Timestamp ? data.validFrom.toDate() : data.validFrom ? new Date(data.validFrom) : null,
          validTo: data.validTo instanceof Timestamp ? data.validTo.toDate() : data.validTo ? new Date(data.validTo) : null,
          validDates: (data.validDates as any[])?.map((dateObj: any) => 
            dateObj instanceof Timestamp ? dateObj.toDate() : new Date(dateObj)
          ) || [],
          selectedFlashIds: data.selectedFlashIds as string[] || [],
          isAvailable: data.isAvailable as boolean,
          createdAt: data.createdAt?.toDate() || new Date(),
          artistId: data.artistId as string | undefined,
          address: (data.address as string) || '',
          city: (data.city as string) || '',
          country: (data.country as string) || '',
        }
        setDeal(dealData)
      } else {
        setDeal(null)
      }
      setLoading(false)
    }, (err) => {
      console.error('Error fetching deal by identifier:', err)
      setError(err)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [identifier])

  return { deal, loading, error }
}

